import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const articleHeroImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Summer%20movie%20theater%20atmosphere%20with%20film%20projector%20and%20popcorn%2C%20warm%20golden%20tones%2C%20cozy%20cinema%20vibe%2C%20artistic%20minimal%20style&width=1400&height=500&seq=article-hero-1&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Smartphone%20playing%20video%20on%20minimal%20desk%20with%20headphones%2C%20warm%20ambient%20lighting%2C%20modern%20clean%20aesthetic&width=1400&height=500&seq=article-hero-2&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Documentary%20camera%20lens%20with%20globe%2C%20warm%20earthy%20tones%2C%20artistic%20minimal%20style%2C%20soft%20natural%20light&width=1400&height=500&seq=article-hero-3&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=TV%20drama%20collage%20with%20warm%20cinematic%20lighting%2C%20modern%20living%20room%2C%20artistic%20minimal%20style&width=1400&height=500&seq=article-hero-4&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Variety%20show%20stage%20with%20colorful%20lights%2C%20vibrant%20warm%20atmosphere%2C%20artistic%20composition&width=1400&height=500&seq=article-hero-5&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Chinese%20animation%20art%20style%20collage%2C%20vibrant%20warm%20colors%2C%20creative%20composition%2C%20artistic%20minimal&width=1400&height=500&seq=article-hero-6&orientation=landscape',
};

const ArticlePage = () => {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors no-underline">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const heroImage = articleHeroImages[article.id] || articleHeroImages['article-1'];
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: heroImage,
    publisher: { '@type': 'Organization', name: '极速高清' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://hhxdwl.cn/article/${article.id}` },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hhxdwl.cn' },
      { '@type': 'ListItem', position: 2, name: category?.name || article.category, item: `https://hhxdwl.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://hhxdwl.cn/article/${article.id}` },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 极速高清`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://hhxdwl.cn/article/${article.id}`}
        ogType="article"
        ogImage={heroImage}
        schemaData={[articleSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-16">
        <article>
          <div className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
            <img
              src={heroImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background-50"></div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 -mt-20 relative z-10">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors no-underline">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              {category && (
                <>
                  <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors no-underline">
                    {category.name}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </>
              )}
              <span className="text-foreground-950 font-medium truncate max-w-[200px]">{article.title}</span>
            </nav>

            <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/60">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-3xl font-heading text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200/60">
                <span className="flex items-center gap-1"><i className="ri-user-line"></i> {article.author}</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}阅读</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {article.views.toLocaleString()}次浏览</span>
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:font-heading [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-950"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 mb-16">
                <h2 className="text-xl font-heading text-foreground-950 mb-6">相关推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200/40 transition-all duration-300 no-underline"
                    >
                      <div className="aspect-[5/3] overflow-hidden">
                        <img
                          src={articleHeroImages[related.id] || articleHeroImages['article-1']}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
};

export default ArticlePage;